import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { allProducts } from '../../mocks/products';
import { addToCart } from '../../utils/cart';
import { useToast } from '../../hooks/useToast';
import Toast from '../../components/base/Toast';

const ProductsPage = () => {
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState('All Products');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProducts, setFilteredProducts] = useState(allProducts);
  const { toasts, removeToast, success } = useToast();

  // Get unique categories
  const categories = ['All Products', 'Trending Under $50', ...Array.from(new Set(allProducts.map(p => p.category)))];
  const categoryCounts = categories.map(cat => ({
    name: cat,
    count: cat === 'All Products' 
      ? allProducts.length 
      : cat === 'Trending Under $50'
      ? allProducts.filter(p => p.price < 50).length
      : allProducts.filter(p => p.category === cat).length
  }));

  // Handle navigation state from search or other pages
  useEffect(() => {
    if (location.state) {
      const { category, searchQuery: query } = location.state as any;
      
      if (category) {
        setSelectedCategory(category);
        setSearchQuery('');
      } else if (query) {
        setSearchQuery(query);
        setSelectedCategory('All Products');
      }
      
      // Clear the state after using it
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Listen for catalog reset event
  useEffect(() => {
    const handleCatalogReset = () => {
      setSelectedCategory('All Products');
      setSearchQuery('');
    };

    window.addEventListener('resetCatalogFilter', handleCatalogReset);
    return () => window.removeEventListener('resetCatalogFilter', handleCatalogReset);
  }, []);

  // Filter products based on category and search query
  useEffect(() => {
    let filtered = allProducts;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(query) ||
          product.description.toLowerCase().includes(query) ||
          product.category.toLowerCase().includes(query)
      );
    }

    // Apply category filter
    if (selectedCategory === 'Trending Under $50') {
      filtered = filtered.filter(product => product.price < 50);
    } else if (selectedCategory !== 'All Products') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    setFilteredProducts(filtered);
  }, [selectedCategory, searchQuery]);

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setSearchQuery(''); // Clear search when changing category
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSelectedCategory('All Products');
  };

  const handleAddToCart = (product: typeof allProducts[0]) => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      quantity: 1
    });
    success(`${product.name} added to cart!`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Toast Notifications */}
      <div className="fixed top-0 right-0 z-[9999] pointer-events-none">
        <div className="pointer-events-auto">
          {toasts.map(toast => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </div>
      </div>

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              {searchQuery ? 'Search Results' : selectedCategory === 'Trending Under $50' ? '🔥 Trending Under $50' : 'Our Products'}
            </h1>
            <p className="text-gray-600">
              {searchQuery 
                ? `Showing ${filteredProducts.length} results for "${searchQuery}"`
                : selectedCategory === 'Trending Under $50'
                ? 'Hot-selling items at unbeatable prices - perfect for impulse buys!'
                : 'Discover our curated collection of premium products'
              }
            </p>
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="mt-3 text-sm text-primary hover:text-primary-dark font-medium cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-circle-line mr-1"></i>
                Clear search and show all products
              </button>
            )}
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Categories */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="bg-gray-50 rounded-2xl p-6 sticky top-24">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Categories</h2>
                <div className="space-y-2">
                  {categoryCounts.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategoryChange(cat.name)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                        selectedCategory === cat.name && !searchQuery
                          ? cat.name === 'Trending Under $50'
                            ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md'
                            : 'bg-gradient-to-r from-primary to-accent-green text-white shadow-md'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span className="font-medium">
                        {cat.name === 'Trending Under $50' && '🔥 '}
                        {cat.name}
                      </span>
                      <span className={`text-sm ${
                        selectedCategory === cat.name && !searchQuery ? 'text-white' : 'text-gray-500'
                      }`}>
                        {cat.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* Main Content - Products Grid */}
            <div className="flex-1">
              {filteredProducts.length > 0 ? (
                <>
                  <div className="mb-6 flex items-center justify-between">
                    <p className="text-gray-600">
                      Showing <strong>{filteredProducts.length}</strong> {filteredProducts.length === 1 ? 'product' : 'products'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
                    {filteredProducts.map((product) => (
                      <div
                        key={product.id}
                        className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
                      >
                        {/* Product Image */}
                        <div className="relative h-64 w-full overflow-hidden bg-gray-100">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                          {product.recurring && (
                            <div className="absolute top-4 right-4 bg-accent-green text-white px-3 py-1 rounded-full text-xs font-medium">
                              {product.recurringNote}
                            </div>
                          )}
                          {product.price < 50 && (
                            <div className="absolute top-4 left-4 bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                              🔥 HOT DEAL
                            </div>
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="p-6">
                          <div className="text-sm text-primary font-medium mb-2">
                            {product.category}
                          </div>
                          <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                            {product.name}
                          </h3>
                          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                            {product.description}
                          </p>

                          {/* Price & Commission */}
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <div className="text-2xl font-bold text-gray-900">
                                ${product.price}
                              </div>
                              {product.recurring && (
                                <div className="text-xs text-gray-500">
                                  per {product.recurringNote?.toLowerCase()}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-gray-500">Earn</div>
                              <div className="text-lg font-bold text-accent-green">
                                ${product.commission}
                              </div>
                            </div>
                          </div>

                          {/* Action Button */}
                          <button 
                            onClick={() => handleAddToCart(product)}
                            className="w-full bg-teal-500 text-white py-3 font-semibold hover:bg-teal-600 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                          >
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-shopping-cart-line text-lg"></i>
                            </div>
                            Add to Cart
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                // No Products Found
                <div className="text-center py-16">
                  <div className="w-24 h-24 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-6">
                    <i className="ri-search-line text-5xl text-gray-400"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    No products found
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {searchQuery 
                      ? `We couldn't find any products matching "${searchQuery}"`
                      : 'No products available in this category'
                    }
                  </p>
                  <button
                    onClick={handleClearSearch}
                    className="px-6 py-3 bg-gradient-to-r from-primary to-accent-green text-white rounded-lg font-medium hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
                  >
                    View All Products
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductsPage;